/* eslint-disable array-callback-return */
import { Button as MuiButton, CircularProgress, Tooltip, IconButton, Menu, MenuItem, Divider } from "@mui/material";
import update from "immutability-helper";
import _ from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import SearchSVG from "../../assets/svgs/search.svg";
import axios from "../../axios";
import popupStyles from "../../popup.module.scss";
import { bulkDeletePatientsData, restorePatientData } from "../../services/adt.service";
import { getAccountQuestions } from "../../services/api/user.api";
import { firstAndLastDateOfInput, getCensusFirstDate } from "../../services/data-input.service";
import { AUTH_ROLES } from "../../types/auth.type";
import { expandedColumnsDefault as expandedColumnsDefaultDataInput } from "../../types/pages.type";
import { getAllowedActions, isOnlyHospitalTabAccess, selectedTabAccess } from "../../utilis/common";
import { toDisplayTime, toSaveDate, toSaveEndDate } from "../../utilis/date-formats";
import AddPatient from "../landing/facility/data/add-patient/AddPatient";
import QuestionSetup from "../landing/facility/data/question-setup/QuestionSetup";
import ResidentDetails from "../landing/facility/data/residentDetails";
import PopupDialog from "../popupDialog/PopupDialog";
import Button from "../ui/button/Button";
import DataInputHeaderSelect from "./DataInputHeaderSelect";
import styles from "./DataInput.module.scss";
import EditBedCapacityDialog from "./edit-bed-capacity/EditBedCapacityDialog";
import EditCensusDialog from "./edit-census-dialog/EditCensusDialog";
import NoFacilityDialog from "./no-facility-dialog/NoFacilityDialog";
import Table from "./table/Table";
import { LoadingButton } from "@mui/lab";
import FilterListIcon from '@mui/icons-material/FilterList';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CloseIcon from '@mui/icons-material/Close';
import ViewAgendaOutlinedIcon from '@mui/icons-material/ViewAgendaOutlined';
import TableRowsOutlinedIcon from '@mui/icons-material/TableRowsOutlined';
import OpenInFullOutlinedIcon from '@mui/icons-material/OpenInFullOutlined';
import CloseFullscreenOutlinedIcon from '@mui/icons-material/CloseFullscreenOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditEndDateInputDialog from "./edit-bed-capacity/EditEndDateInputDialog";
import moment from "moment";
import CustomTooltipContentWithAction, { richTooltipSlotProps } from "../shared/CustomTooltipContentWithAction";
import { QUESTION_INPUT_TYPE } from "../../types/question.type";
import { getDynamicTransferTypes, getStaticTransferTypes } from "../../utilis/datainput-common";
import { setPauseDateSlice, setPauseUser } from "../../store/reducers/common.slice";
import { useMediaQuery } from "@mui/system";
import useFacilityId from "../../hooks/useFacilityId";
import useAccountId from "../../hooks/useAccountId";
import { PAGE_TYPE } from "../../types/pages.type";

const normalizePatientField = (value) =>
	String(value ?? "")
		.replace(/[^\w\s]/gi, "")
		.toLowerCase();

const getSearchWords = (searchTerm) => {
	const trimmed = searchTerm?.trim() ?? "";
	if (!trimmed) return [];

	return trimmed
		.replace(/[^\w\s]/gi, "")
		.split(/\s+/)
		.filter((word) => word.length > 0)
		.map((word) => word.toLowerCase());
};

const patientMatchesSearch = (patient, searchWords) => {
	if (!searchWords.length) return true;

	const firstName = normalizePatientField(patient?.firstName);
	const lastName = normalizePatientField(patient?.lastName);
	const middleInitial = normalizePatientField(patient?.middleInitial);

	const w0 = searchWords[0] ?? "";
	const w1 = searchWords[1] ?? "";
	const w0First = w0[0] ?? "";
	const w1First = w1[0] ?? "";

	if (searchWords.length === 1) {
		return (
			firstName.includes(w0) ||
			lastName.includes(w0) ||
			(Boolean(middleInitial) && Boolean(w0First) && middleInitial.startsWith(w0First))
		);
	}

	return (
		(firstName.includes(w0) && lastName.includes(w1)) ||
		(lastName.includes(w0) && firstName.includes(w1)) ||
		(lastName.includes(w0) && middleInitial.startsWith(w1First)) ||
		(middleInitial.startsWith(w0First) && lastName.includes(w1)) ||
		(firstName.includes(w0) && middleInitial.startsWith(w1First)) ||
		(middleInitial.startsWith(w0First) && firstName.includes(w1))
	);
};

const filterPatientsBySearch = (patients, searchTerm) => {
	const list = Array.isArray(patients) ? patients : [];
	const trimmed = searchTerm?.trim() ?? "";
	if (!trimmed) return list;

	const searchWords = getSearchWords(trimmed);
	if (!searchWords.length) return list;

	return list.filter((patient) => patientMatchesSearch(patient, searchWords));
};

const DataInput = () => {
	const [expandedView, setExpandedView] = useState(false);
	const [loading, setLoading] = useState(true);
	const [type, setType] = useState("all");
	const [patients, setPatients] = useState([]);
	const [fromDate, setFromDate] = useState(moment().subtract(1, "month").format("YYYY-MM-DD"));
	const [toDate, setToDate] = useState(moment().format("YYYY-MM-DD"));
	const [search, setSearch] = useState("");
	const { permission } = useSelector(({ permission }) => ({ permission }));
	const [isEndDateOfADT, setIsEndDateOfADT] = useState(false);
	const [deleteReason, setDeleteReason] = useState("");

	const expandedColumnsDefault = useMemo(() => {
		if (permission?.isMultipleFacility) {
			return [
				{
					Header: "Facility",
					accessor: "facilityId.name",
					isDefault: true
				},
				...expandedColumnsDefaultDataInput,
			];
		}
		return [...expandedColumnsDefaultDataInput];
	}, [permission?.isMultipleFacility]);

	const [transferType, setTransferType] = useState("unplannedHospitalTransfer");
	const [admissionType, setAdmissionType] = useState("allAdmissions");
	const [patientsOriginal, setPatientsOriginal] = useState([]);
	const [selectedResident, setSelectedResident] = useState(undefined);
	const [showAdd, setShowAdd] = useState(false);
	const [editResident, setEditResident] = useState(undefined);
	const isPatientFormOpen = showAdd || Boolean(editResident);
	const [showSettings, setShowSettings] = useState(false);
	const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
	const [listDensity, setListDensity] = useState("comfortable");
	const [showSetup, setShowSetup] = useState(false);
	const [hasInput, setHasInput] = useState(false);
	const [hasInputLoading, setHasInputLoading] = useState(true);
	const [startDate, setStartDate] = useState("");
	const [censusCount, setCensusCount] = useState("");
	const [bedCapacity, setBedCapacity] = useState(null);
	const [adtStepCount, setAdtStepCount] = useState(1);
	const [isEditCensus, setIsEditCensus] = useState(false);
	const [isEditBedCapacity, setIsEditBedCapacity] = useState(false);
	const [hasFacility, setHasFacility] = useState(true);
	const [isOpenNoFacility, setIsOpenNoFacility] = useState(false);
	const [expandedAdmissionsColumns, setExpandedAdmissionsColumns] = useState(expandedColumnsDefault);
	const [expandedReAdmissionsColumns, setExpandedReAdmissionsColumns] = useState(expandedColumnsDefault);
	const [expandedReturnsColumns, setExpandedReturnsColumns] = useState(expandedColumnsDefault);
	const { auth } = useSelector(({ auth }) => ({ auth }));
	const isMobile = useMediaQuery("(max-width: 768px)");

	const canDeletePatients = useCallback(() => {
		if (!permission) return false;

		if (permission.role === AUTH_ROLES.USER) {
			const facilityAccess = permission.facilityAccess;
			if (!facilityAccess) return false;

			// Allow delete if user has write access and is not on mobile
			return facilityAccess.write && !isMobile;
		} else {
			// Non-USER roles can delete if not on mobile
			return !isMobile;
		}
	}, [permission, isMobile]);


	// Filter menu state
	const [filterAnchorEl, setFilterAnchorEl] = useState(null);
	const { endDateOfADT, selectedAccount } = useSelector((state) => state.common);

	const isOnlyHospitalDashboard = useMemo(() => {
		return isOnlyHospitalTabAccess();
	}, []);

	const { allowTransfer, allowAdmission, allowCommunityTransfer, allowDeceased, allowHospitalTransfer } = getAllowedActions(selectedTabAccess());

	const questionSetupDefaults = useMemo(() => {
		const dashboardAccess = selectedAccount?.dashboardAccess ?? [PAGE_TYPE.HOSPITAL, PAGE_TYPE.COMMUNITY_TRANSFER, PAGE_TYPE.DECEASED, PAGE_TYPE.ADMISSION, PAGE_TYPE.OVERALL];

		if (dashboardAccess.includes(PAGE_TYPE.HOSPITAL)) {
			return { type: "transfer", transferType: "hospitalTransfer" };
		} else if (dashboardAccess.includes(PAGE_TYPE.COMMUNITY_TRANSFER)) {
			return { type: "transfer", transferType: "totalCommunity" };
		} else if (dashboardAccess.includes(PAGE_TYPE.DECEASED)) {
			return { type: "transfer", transferType: "deceased" };
		} else if (dashboardAccess.includes(PAGE_TYPE.ADMISSION)) {
			return { type: "admission", transferType: null };
		}
		return { type: "admission", transferType: null };
	}, [selectedAccount?.dashboardAccess]);

	useEffect(() => {
		if (isOnlyHospitalDashboard) {
			setType("transfer");
		}
	}, [isOnlyHospitalDashboard]);

	useEffect(() => {
		if (endDateOfADT && moment(endDateOfADT).isAfter(moment(), 'day')) {
			setToDate(moment(endDateOfADT).format("YYYY-MM-DD"));
		}
	}, [endDateOfADT]);

	// Filter menu handlers
	const handleFilterMenuOpen = (event) => {
		setFilterAnchorEl(event.currentTarget);
	};

	const handleFilterMenuClose = () => {
		setFilterAnchorEl(null);
	};

	const handleQuickFilter = (filterType) => {
		const today = moment();
		let newFromDate, newToDate;

		switch (filterType) {
			case 'lastWeek':
				newFromDate = today.clone().subtract(1, 'week').startOf('week').format('YYYY-MM-DD');
				newToDate = today.clone().subtract(1, 'week').endOf('week').format('YYYY-MM-DD');
				break;
			case 'lastMonth':
				newFromDate = today.clone().subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
				newToDate = today.clone().subtract(1, 'month').endOf('month').format('YYYY-MM-DD');
				break;
			case 'lastYear':
				newFromDate = today.clone().subtract(1, 'year').startOf('year').format('YYYY-MM-DD');
				newToDate = today.clone().subtract(1, 'year').endOf('year').format('YYYY-MM-DD');
				break;
			case 'thisWeek':
				newFromDate = today.clone().startOf('week').format('YYYY-MM-DD');
				newToDate = today.clone().endOf('week').format('YYYY-MM-DD');
				break;
			case 'thisMonth':
				newFromDate = today.clone().startOf('month').format('YYYY-MM-DD');
				newToDate = today.clone().endOf('month').format('YYYY-MM-DD');
				break;
			case 'thisYear':
				newFromDate = today.clone().startOf('year').format('YYYY-MM-DD');
				newToDate = today.clone().endOf('year').format('YYYY-MM-DD');
				break;
			case 'custom':
				// For custom, just close the menu - user can manually select dates
				break;
			default:
				return;
		}

		if (newFromDate && newToDate) {
			setFromDate(newFromDate);
			setToDate(newToDate);
		}

		handleFilterMenuClose();
	};

	const types = useMemo(() => {
		if (!isOnlyHospitalDashboard) {
			return [
				{
					value: "all",
					label: "All ADT",
				},
				{
					value: "admissionReadmissions",
					label: "Admission",
				},
				{
					value: "return",
					label: "Hospital Return",
				},
				{
					value: "transfer",
					label: "Transfer",
				},
				...canDeletePatients ? [{
					value: "deleted",
					label: "Deleted",
				}] : [],
			];
		} else {
			return [
				...allowAdmission ? [{
					value: "admissionReadmissions",
					label: "Admission",
				}] : [],
				...allowTransfer ? [{
					value: "transfer",
					label: "Transfer",
				}] : [],
				...canDeletePatients ? [{
					value: "deleted",
					label: "Deleted",
				}] : []
			];
		}
	}, [allowAdmission, allowTransfer, isOnlyHospitalDashboard, canDeletePatients]);

	const admissionTypes = useMemo(() => {
		return [
			{
				value: "allAdmissions",
				label: "All Admissions",
				subOption: false,
			},
			{
				value: "admission",
				label: "Admission",
			},
			{
				value: "reAdmission",
				label: "Re-Admission",
			},
		];
	}, []);

	const transferTypes = useMemo(() => {
		if (!isOnlyHospitalDashboard) {
			return getStaticTransferTypes();
		}

		return getDynamicTransferTypes({
			allowHospitalTransfer,
			allowCommunityTransfer,
			allowDeceased
		});
	}, [isOnlyHospitalDashboard, allowHospitalTransfer, allowCommunityTransfer, allowDeceased]);

	let transferTypesColumns = {};
	transferTypes.map((ele) => {
		transferTypesColumns[ele.value] = expandedColumnsDefault;
	});
	const [expandedAllTransferColumns, setExpandedAllTransferColumns] = useState(transferTypesColumns);

	const [deletePatientState, setDeletePatientState] = useState({
		state: false,
		patientId: null,
	});
	const [restorePatientState, setRestorePatientState] = useState({
		state: false,
		patientId: null,
	});
	const [deletePatientLoading, setDeletePatientLoading] = useState(false);
	const { facilities: activeFacilities } = useSelector((state) => state.activeFacilities);
	const [initialDate, setInitialDate] = useState(null);
	const [defaultBedCapacity, setDefaultBedCapacity] = useState(0);
	const [firstAndLastData, setFirstAndLastData] = useState(null);
	const dispatch = useDispatch();
	const { facilityId: facilityIdFromHook } = useFacilityId();
	const { accountId: accountIdFromHook } = useAccountId();
	useEffect(() => {
		const fetchCensusDate = async () => {
			const facilityId = activeFacilities[activeFacilities.length - 1];
			const censusData = await getCensusFirstDate(facilityId, activeFacilities);
			if (censusData?.pauseDate) {
				dispatch(setPauseDateSlice(censusData?.pauseDate));
				dispatch(setPauseUser(censusData));
			} else {
				dispatch(setPauseDateSlice(null));
				dispatch(setPauseUser(censusData));
			}
		};

		const fetchFirstAndLastData = async () => {
			const firstAndLastDataResponse = await firstAndLastDateOfInput({
				facilityIds: activeFacilities,
			});
			if (firstAndLastDataResponse) {
				setFirstAndLastData(firstAndLastDataResponse);
			}
		};
		fetchFirstAndLastData();
		fetchCensusDate();
		// eslint-disable-next-line no-use-before-define
	}, [activeFacilities]); // eslint-disable-line react-hooks/exhaustive-deps

	const getTooltipText = (text, count = 40, variant = "body2") => {
		return (
			<span>
				{text && text.length > count ? (
					<Tooltip
						disableFocusListener
						title={<CustomTooltipContentWithAction text={text} />}
						arrow
						componentsProps={richTooltipSlotProps}
					>
						<span style={{ cursor: "pointer" }}>{text?.substring(0, count) + "..."}</span>
					</Tooltip>
				) : (
					<span style={{ cursor: "pointer" }}>{text || ""}</span>
				)}
			</span>
		);
	};

	const getQuestionAnswer = (question, row) => {
		const { accessor, type, validationType, multiple, customQuestionInputType, invisibleAnswerOptions, dataInputLabel } = question;

		if (invisibleAnswerOptions && dataInputLabel) {
			const invisibleLabels = dataInputLabel.split(',').map(label => label.trim()).filter(Boolean);
			if (invisibleLabels.includes(row[accessor])) {
				return row[accessor];
			}
		}

		if (
			customQuestionInputType === QUESTION_INPUT_TYPE.UNLIMITED_ANSWERS &&
			question?.validationOptions?.length > 0
		) {
			const foundOption = question.validationOptions.find(
				(option) => option._id === row[accessor]
			);
			if (foundOption) {
				return foundOption.label;
			}
		}
		switch (type) {
			case "text":
				return row[accessor] ? getTooltipText(row[accessor]) : null;
			case "toggle":
				return row[accessor] ? "Yes" : "No"; // Simplified boolean check
			case "date":
				return row[accessor] ? toDisplayTime(row[accessor], "MM/DD/YYYY") : "-";
			case "time":
				return row[accessor] || null; // More concise way to return value or null
			case "validation":
				if (multiple) {
					const labels = row[validationType]?.map(prodData => prodData.label).filter(Boolean); // Ensure labels exist
					const answer = labels?.join(", ");
					return answer ? getTooltipText(answer) : "N/A";
				} else {
					return row[validationType]?.label ? getTooltipText(row[validationType].label) : "N/A";
				}
			default:
				return null; // Handle unknown types gracefully
		}
	};

	const getTableColumns = (data, type, transferType = null) => {
		if (type === "transfer") {
			const questions = _.filter(data, {
				forType: type,
				forTransferType:
					transferType === "unplannedHospitalTransfer" || transferType === "plannedHospitalTransfer"
						? "hospitalTransfer"
						: transferType,
			});
			const dynamicColumns = questions.map((ele) => {
				return {
					Header: ele?.order?.tableLabel || ele?.order?.label || ele?.question?.tableLabel || ele?.question?.label,
					accessor: ele?.question?.accessor,
					Cell: ({ row = null }) => {
						if (row) {
							const transferValue = getQuestionAnswer(ele?.question, row.original);
							return transferValue ? transferValue : "N/A";
						} else {
							return "N/A";
						}
					},
				};
			});
			return dynamicColumns;
		} else {
			const questions = _.filter(data, { forType: type });
			const dynamicColumns = questions.map((ele) => {
				return {
					Header: ele?.order?.tableLabel || ele?.order?.label || ele?.question?.tableLabel || ele?.question?.label,
					accessor: ele?.question?.accessor,
					Cell: ({ row }) => getQuestionAnswer(ele?.question, row.original),
				};
			});
			return dynamicColumns;
		}
	};

	const getQuestions = async (activeFacilitiesData) => {
		const res = await getAccountQuestions({
			facilityid: activeFacilitiesData,
			accountId: auth.accountId,
		});
		if (res && res.length > 0) {
			const admissionColumns = getTableColumns(res, "admission");
			setExpandedAdmissionsColumns([...expandedColumnsDefault, ...admissionColumns]);

			const reAdmissionColumns = getTableColumns(res, "admission");
			setExpandedReAdmissionsColumns([...expandedColumnsDefault, ...reAdmissionColumns]);

			const returnColumns = getTableColumns(res, "return");
			setExpandedReturnsColumns([...expandedColumnsDefault, ...returnColumns]);

			await Promise.all(
				transferTypes.map(async (ele) => {
					let transferColumns;
					if (ele.value === "unplannedHospitalTransfer" || ele.value === "plannedHospitalTransfer") {
						transferColumns = getTableColumns(res, "transfer", "hospitalTransfer");
					} else if (
						ele.value === "safeDischarge" ||
						ele.value === "SNF" ||
						ele.value === "AMA" ||
						ele.value === "deceased"
					) {
						transferColumns = getTableColumns(res, "transfer", ele.value);
					}

					if (transferColumns) {
						await setExpandedAllTransferColumns((prev) => ({
							...prev,
							[ele.value]: [...expandedColumnsDefault, ...transferColumns],
						}));
					}
				})
			);
			return;
		}
	};

	useEffect(() => {
		getQuestions(activeFacilities);
		// eslint-disable-next-line
	}, [activeFacilities]);

	const getInitialCensusDate = async () => {
		const facilityId = facilityIdFromHook;
		await getCensusFirstDate(facilityId).then((res) => {
			if (res) {
				setInitialDate(res?.date);
				setDefaultBedCapacity(res?.bedCapacity);
			}
		});
	};

	useEffect(() => {
		getInitialCensusDate();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [facilityIdFromHook]);

	useEffect(() => {
		if (activeFacilities.length > 0) {
			const timer = setTimeout(() => {
				if (
					(activeFacilities[activeFacilities.length - 1] === undefined ||
						activeFacilities[activeFacilities.length - 1] == null) &&
					(permission.role === AUTH_ROLES.SUPER ||
						permission.role === AUTH_ROLES.OWNER ||
						permission.role === AUTH_ROLES.REGIONAL)
				) {
					setHasFacility(false);
					setIsOpenNoFacility(true);
				}
				if (
					activeFacilities[activeFacilities.length - 1] === undefined ||
					activeFacilities[activeFacilities.length - 1] == null
				) {
					setHasFacility(false);
				}
			}, 1500);
			return () => clearTimeout(timer);
		}
	}, [permission, activeFacilities]);

	const hasInputAction = async () => {
		setHasInputLoading(true);
		if (activeFacilities.length > 0) {
			let re = await axios.get(`/api/facility/has-census/?facilityId=${activeFacilities[activeFacilities.length - 1]}`);
			if (re?.data) {
				setHasInput(re?.data);
			}
		}
		setHasInputLoading(false);
	};

	const closePatientForm = () => {
		setShowAdd(false);
		setEditResident(undefined);
	};

	const openAddPatient = () => {
		setEditResident(undefined);
		setShowAdd(true);
	};

	const openEditPatient = (resident) => {
		setShowAdd(false);
		setEditResident(resident);
	};

	const patientUpdated = (data) => {
		if (!data) return;
		if (type && (type.toLowerCase() === data?.type?.toLowerCase() || type === "all" || (type === "allAdmissions" && data?.type?.toLowerCase() === "admission") || (type === "allReAdmissions" && data?.type?.toLowerCase() === "readmission"))) {
			setPatientsOriginal((prev) => {
				const list = Array.isArray(prev) ? prev : [];
				const origIdx = list.findIndex((p) => p._id === data._id);
				if (origIdx === -1) return list;
				return update(list, { [origIdx]: { $set: data } });
			});
		} else {
			setPatientsOriginal((prev) =>
				(Array.isArray(prev) ? prev : []).filter((item) => item._id !== data._id)
			);
		}
	};

	const getData = async (signal) => {
		setLoading(true);
		const facilityId = activeFacilities[activeFacilities.length - 1];
		if (facilityId) {
			let formDateLatest = toSaveDate(fromDate, "YYYY-MM-DD");
			let toDateLatest = toSaveEndDate(toDate, "YYYY-MM-DD");
			await axios
				.get(
					`/api/patient/${facilityId}`,
					{
						signal: signal,
						params: {
							activeFacilities: activeFacilities,
							type: type,
							...(type !== "deleted" && {
								fromdate: formDateLatest,
								todate: toDateLatest,
							}),
							...type === "admissionReadmissions" && { admissionType },
							...type === "transfer" && { transfertype: transferType },
						}
					}
				)
				.then((res) => {
					setLoading(false);
					if (res && res.data) {
						const data = Array.isArray(res.data) ? res.data : [];
						setPatientsOriginal(data);
					}
				})
				.catch((e) => { });
		}
	};

	const deletePatient = async (id, reason = "") => {
		if (!canDeletePatients) return;
		const res = await bulkDeletePatientsData([id,], reason);

		if (res?.deletedIds?.length) {
			setPatientsOriginal((prev) =>
				(Array.isArray(prev) ? prev : []).filter((f) => !res.deletedIds.includes(f._id))
			);
		}
	};

	const restorePatient = async (id, reason = "") => {
		if (!canDeletePatients) return;
		const res = await restorePatientData([id,], reason);

		if (res?._id) {
			setPatientsOriginal((prev) =>
				(Array.isArray(prev) ? prev : []).filter((f) => f._id !== res?._id)
			);
		}
	};

	useEffect(() => {
		const fetchData = async (signal) => {
			await getData(signal);
			await hasInputAction();
		};
		if (!type || !fromDate || !toDate || activeFacilities.length === 0) {
			setHasInputLoading(false);
			setLoading(false);
			setPatients([]);
			setPatientsOriginal([]);
			return;
		}
		const controller = new AbortController();
		const signal = controller.signal;
		fetchData(signal);
		return () => {
			controller.abort();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		type,
		fromDate,
		toDate,
		transferType,
		admissionType,
		// eslint-disable-next-line
		activeFacilities,
	]);

	// *********************
	// eslint-disable-next-line
	const columns = useMemo(() => {
		const baseColumns = [
			{
				Header: "Last Name",
				accessor: "lastName",
			},
			{
				Header: "First Name",
				accessor: "firstName",
			},
			{
				Header: "Middle Initial",
				accessor: "middleInitial",
			},
			{
				Header: "Type",
				accessor: "type",
				Cell: ({ row }) => (
					<div>
						{row.original.type === "transfer"
							? row.original.transferType.replace(/([A-Z])/g, " $1").replace(/^./, function (str) {
								return str.toUpperCase();
							})
							: row.original.type.replace(/^./, function (str) {
								return str.toUpperCase();
							})}
					</div>
				),
			},
			{
				Header: "Date of transfer",
				accessor: "dateOfADT",
				Cell: ({ row }) => <div>{toDisplayTime(row.original.dateOfADT)}</div>,
			},
		];

		if (permission?.isMultipleFacility) {
			return [{
				Header: "Facility",
				accessor: "facilityId.name",
				isDefault: true
			}, ...baseColumns];
		}

		return baseColumns;
	}, [permission?.isMultipleFacility]);

	useEffect(() => {
		setPatients(filterPatientsBySearch(patientsOriginal, search));
	}, [search, patientsOriginal]);

	const patientAdded = (patient) => {
		if (!patient) return;
		if (type && (type.toLowerCase() === patient?.type?.toLowerCase() || type === "all" || (type === "allAdmissions" && patient?.type?.toLowerCase() === "admission") || (type === "allReAdmissions" && patient?.type?.toLowerCase() === "readmission"))) {
			setPatientsOriginal((prev) => {
				const list = Array.isArray(prev) ? [...prev] : [];
				if (list.some((p) => p._id === patient._id)) return list;
				return update(list, { $push: [patient] });
			});
		}
	};

	const getDate = () => {
		return ` ${moment(startDate).add(-1, "day").endOf("day").format("MMMM DD YYYY hh:mm A")}`;
	};

	const saveCensus = async () => {
		setHasInputLoading(true);
		let re = await axios.post(`/api/census`, {
			count: !isOnlyHospitalTabAccess() ? censusCount : null,
			bedCapacity: bedCapacity,
			date: toSaveDate(startDate, "YYYY-MM-DD"),
			accountId: accountIdFromHook,
			facilityId: activeFacilities[activeFacilities.length - 1],
			isBaseline: true,
		});
		setHasInput(re.data);
		setHasInputLoading(false);
		getInitialCensusDate();
	};

	const saveInputDateNext = useCallback((count) => {
		setAdtStepCount(count);
	}, []);

	const saveInputDatePrev = useCallback((count) => {
		setAdtStepCount(count);
	}, []);

	const cancelConfirmationDialog = () => {
		setAdtStepCount(1);
		setShowConfirmationDialog(false);
	};

	const onSaveConfirmDialog = () => {
		setShowConfirmationDialog(false);
		saveCensus();
	};

	const handleDelete = useCallback((patientId) => {
		setDeletePatientState({ state: true, patientId });
	}, []);

	const handleRecover = useCallback((patientId) => {
		setRestorePatientState({ state: true, patientId });
	}, []);

	const dateOfADTColumn = {
		Header: "Date of ADT",
		accessor: "dateOfADT",
		Cell: ({ row }) => <div>{toDisplayTime(row.original.dateOfADT)}</div>,
	};

	const appendDateOfADTIfMissing = (columns) => {
		if (!Array.isArray(columns)) {
			return columns;
		}
		if (columns.some((col) => col.accessor === "dateOfADT")) {
			return columns;
		}
		return [...columns, dateOfADTColumn];
	};

	const getTransferTypesColumn = () => {
		if (type === "transfer") {
			return appendDateOfADTIfMissing(expandedAllTransferColumns[transferType]);
		} else if (type === "return") {
			return appendDateOfADTIfMissing(expandedReturnsColumns);
		} else if (type === "admissionReadmissions") {
			if (admissionType === "admission") {
				return appendDateOfADTIfMissing(expandedAdmissionsColumns);
			} else if (admissionType === "reAdmission") {
				return appendDateOfADTIfMissing(expandedReAdmissionsColumns);
			} else {
				return appendDateOfADTIfMissing(expandedColumnsDefaultDataInput);
			}
		} else if (type === "all") {
			return appendDateOfADTIfMissing(expandedColumnsDefaultDataInput);
		}
		else if (type === "deleted") {
			const purgeAfterColumn = {
				Header: "Purge After",
				accessor: "purgeAfter",
				Cell: ({ row }) => <div>{toDisplayTime(row.original.purgeAfter)}</div>,
			};
			return [...expandedColumnsDefaultDataInput, purgeAfterColumn];
		}
	};

	function getTableColumnsForDetails() {
		switch (type) {
			case "transfer":
				if (transferType === "allTransfers" || transferType === "allHospitalTransfers") {
					return appendDateOfADTIfMissing(expandedColumnsDefaultDataInput);
				}
				return appendDateOfADTIfMissing(expandedAllTransferColumns[transferType]);

			case "all":
				return appendDateOfADTIfMissing(expandedColumnsDefaultDataInput);

			case "admissionReadmissions":
				switch (admissionType) {
					case "admission":
						return appendDateOfADTIfMissing(expandedAdmissionsColumns);
					case "reAdmission":
						return appendDateOfADTIfMissing(expandedReAdmissionsColumns);
					default:
						return appendDateOfADTIfMissing(expandedColumnsDefaultDataInput);
				}

			case "return":
				return appendDateOfADTIfMissing(expandedReturnsColumns);
			case "deleted":
				return appendDateOfADTIfMissing(expandedColumnsDefaultDataInput);

			default:
				return {};
		}
	}

	return (
		<>
			<div className={`page-content ${styles.dataInput}`}>
				{!isPatientFormOpen && (
				<div className={styles.header}>
					<div className={styles.headerFilters}>
						<div className={styles.searchWrpr}>
							<input
								type="text"
								className={styles.searchInpt}
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								placeholder="Search"
							/>
							{search ? (
								<IconButton
									size="small"
									onClick={() => setSearch("")}
									className={styles.clearButton}
									title="Clear search"
								>
									<CloseIcon fontSize="small" />
								</IconButton>
							) : (
								<SearchSVG fill="#4879F5" height="18" width="18" />
							)}
						</div>

						<div className={`${styles.headerField} ${styles.headerFieldType}`}>
							<DataInputHeaderSelect
								className={styles.headerSelect}
								options={types}
								selected={type}
								onChange={setType}
								aria-label="ADT type filter"
							/>
						</div>

						{type === "admissionReadmissions" && (
							<div className={`${styles.headerField} ${styles.headerFieldSub}`}>
								<DataInputHeaderSelect
									className={styles.headerSelect}
									options={admissionTypes}
									selected={admissionType}
									onChange={setAdmissionType}
									aria-label="Admission type filter"
								/>
							</div>
						)}

						{type === "transfer" && (
							<div className={`${styles.headerField} ${styles.headerFieldWide}`}>
								<DataInputHeaderSelect
									className={styles.headerSelect}
									wide
									width={280}
									options={transferTypes}
									selected={transferType}
									onChange={setTransferType}
									aria-label="Transfer type filter"
								/>
							</div>
						)}

						{type !== "deleted" && (
							<>
								<div className={styles.headerDateGroup}>
									<input
										className={`ffmar fs13 ${styles.headerDateInput}`}
										type="date"
										value={fromDate}
										onChange={(e) => setFromDate(e.target.value)}
										aria-label="From date"
									/>
									<input
										className={`ffmar fs13 ${styles.headerDateInput}`}
										type="date"
										value={toDate}
										onChange={(e) => setToDate(e.target.value)}
										aria-label="To date"
									/>
									<IconButton
										onClick={handleFilterMenuOpen}
										className={`${styles.headerIconBtn} ${styles.headerQuickFilterBtn}`}
										title="Quick date filters"
									>
										<FilterListIcon fontSize="small" />
									</IconButton>

									<IconButton
										className={`${styles.headerIconBtn} ${expandedView ? styles.headerIconBtnActive : ""}`}
										onClick={() => setExpandedView(!expandedView)}
										title={expandedView ? "Collapse columns" : "Expand columns"}
									>
										{expandedView ? (
											<CloseFullscreenOutlinedIcon fontSize="small" />
										) : (
											<OpenInFullOutlinedIcon fontSize="small" />
										)}
									</IconButton>

									<div className={styles.listViewToggle}>
										<IconButton
											size="small"
											className={`${styles.toggleBtn} ${listDensity === "comfortable" ? styles.active : ""}`}
											onClick={() => setListDensity("comfortable")}
											title="Comfortable table"
										>
											<ViewAgendaOutlinedIcon fontSize="small" />
										</IconButton>
										<IconButton
											size="small"
											className={`${styles.toggleBtn} ${listDensity === "compact" ? styles.active : ""}`}
											onClick={() => setListDensity("compact")}
											title="Compact list"
										>
											<TableRowsOutlinedIcon fontSize="small" />
										</IconButton>
									</div>
								</div>

								<Menu
									anchorEl={filterAnchorEl}
									open={Boolean(filterAnchorEl)}
									onClose={handleFilterMenuClose}
									PaperProps={{
										style: {
											maxHeight: 400,
											width: 250,
										},
									}}
								>
									<MenuItem onClick={() => handleQuickFilter('lastWeek')}>
										<CalendarTodayIcon className="m-r-10" />
										Last Week
									</MenuItem>
									<MenuItem onClick={() => handleQuickFilter('lastMonth')}>
										<CalendarMonthIcon className="m-r-10" />
										Last Month
									</MenuItem>
									<MenuItem onClick={() => handleQuickFilter('lastYear')}>
										<CalendarMonthIcon className="m-r-10" />
										Last Year
									</MenuItem>
									<Divider />
									<MenuItem onClick={() => handleQuickFilter('thisWeek')}>
										<CalendarTodayIcon className="m-r-10" />
										This Week
									</MenuItem>
									<MenuItem onClick={() => handleQuickFilter('thisMonth')}>
										<CalendarMonthIcon className="m-r-10" />
										This Month
									</MenuItem>
									<MenuItem onClick={() => handleQuickFilter('thisYear')}>
										<CalendarMonthIcon className="m-r-10" />
										This Year
									</MenuItem>
								</Menu>
							</>
						)}

						{type === "deleted" && (
							<div className={styles.listViewToggle}>
								<IconButton
									size="small"
									className={`${styles.toggleBtn} ${listDensity === "comfortable" ? styles.active : ""}`}
									onClick={() => setListDensity("comfortable")}
									title="Comfortable table"
								>
									<ViewAgendaOutlinedIcon fontSize="small" />
								</IconButton>
								<IconButton
									size="small"
									className={`${styles.toggleBtn} ${listDensity === "compact" ? styles.active : ""}`}
									onClick={() => setListDensity("compact")}
									title="Compact list"
								>
									<TableRowsOutlinedIcon fontSize="small" />
								</IconButton>
							</div>
						)}
					</div>

					<div className={styles.headerActions}>
						<button
							type="button"
							className={`${styles.headerMenuBtn} ${showSettings ? styles.headerMenuBtnActive : ""}`}
							onClick={(e) => {
								e.stopPropagation();
								setShowSettings(!showSettings);
							}}
							aria-label="More actions"
						>
							<MoreVertIcon fontSize="small" />
							<div className={`${styles.settingsFlyout} ${showSettings ? styles.active : ""}`}>
									<div
										onClick={(e) => {
											e.stopPropagation();
											setShowSettings(!showSettings);
											setShowSetup(!showSetup);
										}}
										className={`ffmar fs14 ${styles.settingsFlyoutItem}`}
									>
										Customize questions
									</div>
									{!isOnlyHospitalDashboard && !permission.isMultipleFacility && (
										<div
											onClick={(e) => {
												e.stopPropagation();
												setShowSettings(!showSettings);
												setIsEditCensus(!isEditCensus);
											}}
											className={`ffmar fs14 ${styles.settingsFlyoutItem}`}
										>
											Edit census
										</div>
									)}
									{!permission.isMultipleFacility && (
										<div
											onClick={(e) => {
												e.stopPropagation();
												setShowSettings(!showSettings);
												setIsEditBedCapacity(!isEditBedCapacity);
											}}
											className={`ffmar fs14 ${styles.settingsFlyoutItem}`}
										>
											Edit bed capacity
										</div>
									)}
									<div
										onClick={(e) => {
											e.stopPropagation();
											setShowSettings(!showSettings);
											setIsEndDateOfADT(!isEndDateOfADT);
										}}
										className={`ffmar fs14 ${styles.settingsFlyoutItem}`}
									>
										Edit end of ADT date
									</div>
								</div>
						</button>

						{showSetup ? (
							<QuestionSetup
								close={() => setShowSetup(!showSetup)}
								defaultType={questionSetupDefaults.type}
								defaultTransferType={questionSetupDefaults.transferType}
							/>
						) : undefined}
						{permission.isMultipleFacility ? (
							<Tooltip
								title="this feature is disabled when viewing multiple facilities"
							>
								<LoadingButton
									size="medium"
									variant="contained"
									className={styles.addButton}
									sx={{
										padding: "0 20px",
										height: "42px",
										borderRadius: "8px",
										fontSize: "13.5px",
										letterSpacing: "0.02em",
										background: "#4879F5",
										color: "#fff",
										opacity: 0.5,
										textTransform: "none",
										fontWeight: 600,
										"&:hover": {
											boxShadow: "0 4px 12px rgba(72, 121, 245, 0.28)",
											background: "#4879F5",
										},
									}}
								>
									Add ADT
								</LoadingButton>
							</Tooltip>
						) : (
							<MuiButton
								variant="contained"
								disableElevation
								onClick={openAddPatient}
								disabled={!hasInput}
								className={styles.addButton}
								sx={{
									padding: "0 20px",
									height: "42px",
									borderRadius: "8px",
									fontSize: "13.5px",
									letterSpacing: "0.02em",
									background: "#4879F5",
									color: "#fff",
									textTransform: "none",
									fontWeight: 600,
									fontFamily: "'Manrope', sans-serif",
									"&:hover": {
										boxShadow: "0 4px 12px rgba(72, 121, 245, 0.28)",
										background: "#4879F5",
									},
									"&.Mui-disabled": {
										background: "#4879F5",
										color: "#fff",
										opacity: 0.5,
									},
								}}
							>
								Add ADT
							</MuiButton>
						)}

					</div>
				</div>
				)}
				<div className={styles.tableWrapper}>
					{activeFacilities.length > 0 && (
						<div className={styles.listWrpr} style={{ position: "relative" }}>
							{(loading || hasInputLoading) && (
								<div className={styles.loadingOverlay}>
									<div className={styles.loadingContent}>
										<CircularProgress />
										<p className={styles.loadingText}>Loading...</p>
									</div>
								</div>
							)}
							{!hasInputLoading && (
								hasInput ? (
									<Table
										isMultipleFacility={permission?.isMultipleFacility}
										canDelete={canDeletePatients}
										columns={
											!expandedView
												? columns
												: getTransferTypesColumn()
										}
										loading={loading}
										data={patients}
										initialDate={initialDate}
										setSelectedResident={setSelectedResident}
										selectedResident={selectedResident}
										patientUpdated={patientUpdated}
										handleDelete={handleDelete}
										type={type}
										handleRecover={handleRecover}
										densityMode={listDensity}
										onEditPatient={openEditPatient}
										key={"has-input-table-data-input"}
									/>
								) : (
									<div className={`df acc ffc ${styles.addCensusStartWrpr}`}>
										{adtStepCount === 1 && (
											<>
												<p className={`ffmar fw600 fs18`}>
													Before you can add ADT, we need to know the date for which you will start entering ADT data.
												</p>
												<div className={`df ffc acc`}>
													<input
														type="date"
														className={`ffmar fs13 m-t-25 ${styles.dateInput}`}
														value={startDate}
														disabled={!hasFacility}
														onChange={(e) => {
															setStartDate(e.target.value);
														}}
													/>
													<div className={`w100 m-t-20`}>
														<Button
															text="Next"
															buttonStyle="theme"
															size="large"
															w100={true}
															disabled={!startDate || !hasFacility}
															className={styles.censusButton}
															action={() => saveInputDateNext(isOnlyHospitalDashboard ? 3 : 2)}
														/>
													</div>
												</div>
											</>
										)}
										{adtStepCount === 2 && (
											<>
												<p className={`ffmar fw600 fs18`}>
													Now Please enter your facility's census as of
													{getDate()}.
												</p>
												<div className={`df ffc acc`}>
													<input
														className={`tac m-t-30 ${styles.censusInput}`}
														value={censusCount}
														onChange={(e) => {
															const re = /^[0-9\b]+$/;
															if (e.target.value === "" || re.test(e.target.value)) {
																setCensusCount(e.target.value);
															}
														}}
													/>
													<div className={`w100 m-t-20 ${styles.censusButtonContainer}`}>
														<Button
															text="Back"
															buttonStyle="primary"
                              size="large"
															className={styles.censusButtonPrev}
															action={() => saveInputDatePrev(1)}
														/>
														<Button
															text="Next"
															buttonStyle="theme"
															size="large"
															disabled={!censusCount}
															className={styles.censusButtonSave}
															action={() => saveInputDatePrev(3)}
														/>
													</div>
												</div>
											</>
										)}
										{adtStepCount === 3 && (
											<>
												<p className={`ffmar fw600 fs18`}>What is the bed capacity of your facility?</p>
												<div className={`df ffc acc`}>
													<input
														className={`tac m-t-30 ${styles.censusInput}`}
														value={bedCapacity}
														onChange={(e) => {
															const re = /^[0-9\b]+$/;
															if (e.target.value === "" || re.test(e.target.value)) {
																setBedCapacity(e.target.value);
															}
														}}
													/>
													<div className={`w100 m-t-20 ${styles.censusButtonContainer}`}>
														<Button
															text="Back"
															buttonStyle="primary"
															size="large"
															className={styles.censusButtonPrev}
															action={() => saveInputDatePrev(isOnlyHospitalDashboard ? 1 : 2)}
														/>
														<Button
															text="Save"
															buttonStyle="theme"
															size="large"
															disabled={!bedCapacity}
															className={styles.censusButtonSave}
															action={() => setShowConfirmationDialog(true)}
														/>
													</div>
												</div>
											</>
										)}
									</div>
								)
							)}
						</div>
					)}
				</div>

				{isOpenNoFacility && (
					<NoFacilityDialog close={() => setIsOpenNoFacility(false)} patientAdded={patientAdded} />
				)}
			</div>

			{isPatientFormOpen ? (
				<AddPatient
					selectedResident={editResident}
					initialDate={initialDate}
					close={closePatientForm}
					patientAdded={patientAdded}
					patientUpdated={patientUpdated}
					patientId={editResident?._id}
					step={editResident ? 3 : undefined}
					isMultipleFacility={permission?.isMultipleFacility}
				/>
			) : null}

			{selectedResident ? (
				<ResidentDetails
					details={selectedResident}
					close={() => setSelectedResident(undefined)}
					tableColumns={getTableColumnsForDetails()}
				/>
			) : null}

			{isEditCensus && <EditCensusDialog close={() => setIsEditCensus(false)} />}
			{isEditBedCapacity && (
				<EditBedCapacityDialog
					setDefaultBedCapacity={setDefaultBedCapacity}
					bedCapacity={defaultBedCapacity}
					close={() => setIsEditBedCapacity(false)}
				/>
			)}
			{isEndDateOfADT && (
				<EditEndDateInputDialog
					endDateOfADT={endDateOfADT}
					close={() => setIsEndDateOfADT(false)}
					firstAndLastData={firstAndLastData}
					activeFacilities={activeFacilities}
				/>
			)}
			{showConfirmationDialog && (
				<>
					<div className={popupStyles.overlay}></div>
					<div className={popupStyles.addPopup}>
						<div className={`df aic ${popupStyles.hdr}`}>
							<p className={`ffmm fs18`}>Confirmation</p>
						</div>{" "}
						{!isOnlyHospitalTabAccess() ? (
							<div className={`${popupStyles.popupCntnt} ${styles.popupCntnt}`}>
								Are you sure you want to set{" "}
								<span className={styles.importantSpan}>{moment(startDate).format("MM/DD/YYYY")}</span> as starting ADT
								date and <br />
								<span className={styles.importantSpan}>{censusCount}</span> as the census count with{" "}
								<span className={styles.importantSpan}>{bedCapacity}</span> bad capacity ?
							</div>
						) : (
							<div className={`${popupStyles.popupCntnt} ${styles.popupCntnt}`}>
								Are you sure you want to set{" "}
								<span className={styles.importantSpan}>{moment(startDate).format("MM/DD/YYYY")}</span> as starting ADT
								date and <br />
								<span className={styles.importantSpan}>{bedCapacity}</span>
								{" bad capacity ?"}
							</div>
						)}
						<div className={`${popupStyles.popupFtr}`}>
							<div className={`df aic ${popupStyles.actionsWrrp}`}>
								<div className={`mla`}>
									<button className={`ffmr fs14 m-r-8 ${popupStyles.text}`} onClick={cancelConfirmationDialog}>
										Cancel
									</button>
									<button className={`ffmr fs14 ${popupStyles.primary}`} onClick={onSaveConfirmDialog}>
										Save
									</button>
								</div>
							</div>
						</div>
					</div>
				</>
			)}

			<PopupDialog
				state={deletePatientState.state}
				isDestructiveAction={true}
				isLoading={deletePatientLoading}
				onClose={() => {
					setDeleteReason("");
					setDeletePatientState({ state: false, patientId: null });
				}}
				onConfirm={async () => {
					setDeletePatientLoading(true);
					await deletePatient(deletePatientState.patientId, deleteReason);
					setDeletePatientLoading(false);
					setDeletePatientState({ state: false, patientId: null });
					setDeleteReason("");
				}}
				dialogMessage={"Are you sure you want to delete this ADT?"}
				confirmBtnText={"Delete"}
			>
				<textarea
					placeholder="Optional reason"
					value={deleteReason}
					onChange={(e) => setDeleteReason(e.target.value)}
					style={{ width: "100%", minHeight: "80px", marginTop: "10px" }}
				/>
			</PopupDialog>
			<PopupDialog
				state={restorePatientState.state}
				isDestructiveAction={false}
				isLoading={false}
				onClose={() => setRestorePatientState({ state: false, patientId: null })}
				onConfirm={async () => {
					await restorePatient(restorePatientState.patientId);
					setRestorePatientState({ state: false, patientId: null });
				}}
				dialogMessage={"Are you sure you want to restore this ADT?"}
				confirmBtnText={"Restore"}
			/>
		</>
	);
};

export default DataInput;
